import { useEffect, useState } from 'react';
// api
import api from 'utils/api';

/**
 * Shared resolution of a stored Spotify artist URL/id into a display preview
 * (avatar + name) — used by the SpotifyUrlField control and the Review row.
 */

/** Spotify artist id from a profile URL or a bare 22-char id. */
export const extractArtistId = (value: string): string | null => {
  const match = value.match(/artist\/([A-Za-z0-9]+)/);
  if (match) return match[1];
  return /^[A-Za-z0-9]{22}$/.test(value.trim()) ? value.trim() : null;
};

/** Shape of GET /spotify/artist/:id (see wiki: Artist Onboarding Profile API). */
export interface IArtistPreview {
  id: string;
  name: string;
  imageUrl: string | null;
}

export interface ISelectedArtist {
  name: string;
  imageUrl?: string | null;
}

/** Resolve a URL/id to the preview; null on anything unresolvable (failures are silent). */
export const fetchArtistPreview = async (value: string): Promise<ISelectedArtist | null> => {
  const id = extractArtistId(value);
  if (!id) return null;
  try {
    const artist = await api.spotify.getArtistById<IArtistPreview>({ id });
    return artist?.name ? { name: artist.name, imageUrl: artist.imageUrl } : null;
  } catch {
    return null;
  }
};

/** Preview for a stored value; null while unresolved (callers fall back to the raw URL). */
export const useSpotifyArtistPreview = (value?: string | null) => {
  const [artist, setArtist] = useState<ISelectedArtist | null>(null);

  useEffect(() => {
    setArtist(null);
    if (!value) return undefined;
    let cancelled = false;
    fetchArtistPreview(value).then((resolved) => {
      if (!cancelled && resolved) setArtist(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);

  return artist;
};
